<template>
  <div class="chat-announcements-feed">
    <div v-if="loading" class="chat-announcements-feed__state text-400-default neutral_2">Đang tải...</div>
    <div v-else-if="!items.length" class="chat-announcements-feed__state text-400-default neutral_2">{{ emptyText }}</div>
    <ul v-else class="chat-announcements-feed__list">
      <li
        v-for="item in items"
        :key="item.message_id + '-' + item.group_id"
        class="chat-announcements-feed__item"
        role="button"
        tabindex="0"
        @click="onSelect(item)"
        @keydown.enter.prevent="onSelect(item)"
      >
        <div class="chat-announcements-feed__title text-400-medium neutral_1">{{ item.title || 'Thông báo' }}</div>
        <div v-if="showGroupName && item.chat_group_name" class="chat-announcements-feed__group-line text-300-default neutral_2">
          {{ item.chat_group_name }}
        </div>
        <div class="chat-announcements-feed__sender-row">
          <image-template
            class="chat-announcements-feed__avatar"
            :width="'22px'"
            :height="'22px'"
            :size="'small'"
            :image="item.sender && item.sender.avatar_url"
            :thumbnail="item.sender && item.sender.thumbnail"
            :name="senderLine(item)"
          />
          <span class="chat-announcements-feed__meta text-300-default neutral_2">{{ senderLine(item) }}</span>
        </div>
        <div v-if="item.expires_at" class="chat-announcements-feed__expires text-300-default neutral_3">
          Hết hạn: {{ formatExpires(item.expires_at) }}
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import moment from 'moment';
import ChatService from '../../../../../store/services/ChatService';
import { navigateToChatAnnouncement } from '../../../../../helper/chatAnnouncementNav';
import ImageTemplate from '../../../../../components/ImageTemplate';

export default {
  name: 'ChatAnnouncementsFeed',
  components: { ImageTemplate },
  props: {
    /** `cross_group` — API recent toàn nhóm user tham gia; `group` — theo một nhóm */
    mode: {
      type: String,
      default: 'group',
      validator: (v) => ['cross_group', 'group'].indexOf(v) !== -1,
    },
    /** Khi mode=group; mặc định lấy từ route messenger_id */
    groupId: {
      type: [String, Number],
      default: null,
    },
    limit: {
      type: Number,
      default: 10,
    },
    onlyActive: {
      type: Boolean,
      default: false,
    },
    showGroupName: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'Chưa có thông báo',
    },
    /** true: chỉ emit `select`, không điều hướng (panel full list trong Messager) */
    emitOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      items: [],
    };
  },
  watch: {
    mode() {
      this.load();
    },
    groupId() {
      this.load();
    },
    limit() {
      this.load();
    },
    onlyActive() {
      this.load();
    },
    '$route.params.messenger_id'() {
      if (this.mode === 'group') {
        this.load();
      }
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    resolvedGroupId() {
      if (this.groupId != null && this.groupId !== '') {
        return this.groupId;
      }
      return this.$route.params.messenger_id;
    },
    async load() {
      this.loading = true;
      this.items = [];
      try {
        if (this.mode === 'cross_group') {
          const res = await ChatService.getRecentAnnouncements({ limit: this.limit });
          if (res && res.status && Array.isArray(res.data)) {
            this.items = res.data;
          }
        } else {
          const gid = this.resolvedGroupId();
          if (!gid) {
            this.items = [];
            return;
          }
          const res = await ChatService.getGroupAnnouncements(gid, {
            limit: this.limit,
            only_active: this.onlyActive ? 1 : 0,
          });
          if (res && res.status && Array.isArray(res.data)) {
            this.items = res.data;
          }
        }
      } catch (e) {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    senderLine(item) {
      const s = item.sender;
      if (!s) return '';
      return s.display_name || s.fullname || s.name || '';
    },
    formatExpires(iso) {
      if (!iso) return '';
      return moment(iso).format('DD/MM/YYYY HH:mm');
    },
    onSelect(item) {
      this.$emit('select', item);
      if (this.emitOnly) {
        return;
      }
      navigateToChatAnnouncement(this, {
        groupId: item.group_id,
        messageId: item.message_id,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.chat-announcements-feed__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.chat-announcements-feed__item {
  padding: 10px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover .chat-announcements-feed__title {
    color: #198eeb;
  }
}

.chat-announcements-feed__title {
  line-height: 1.35;
}

.chat-announcements-feed__group-line {
  margin-top: 4px;
  line-height: 1.3;
}

.chat-announcements-feed__sender-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  min-height: 22px;
}

.chat-announcements-feed__avatar {
  width: 22px !important;
  height: 22px !important;
  min-width: 22px;
  min-height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid #eee;
  overflow: hidden;
  aspect-ratio: 1 / 1;

  .image-template__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.chat-announcements-feed__meta {
  margin-top: 0;
  line-height: 1.3;
}

.chat-announcements-feed__expires {
  margin-top: 4px;
  line-height: 1.3;
}

.chat-announcements-feed__state {
  padding: 16px 0;
}
</style>
